import type { ComponentProps, PropsWithChildren } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { ViewProps } from 'react-native';
import { Platform, StyleSheet, View } from 'react-native';
import { type SafeAreaViewProps } from 'react-native-safe-area-context';

import type { Region } from '@sg/garnish';
import { Button, Modal, Navbar, theme, useResponsive } from '@sg/garnish';

import { useIsFoldableDevice, useIsLiquidGlassEnabled } from '../../hooks';
import { FloatingBar } from '../FloatingBar';
import { FloatingControls } from '../FloatingControls';
import { LoadingAnimation } from '../LoadingAnimation';
import { NotFoundView } from '../NotFoundView';
import { TransparentModal } from '../TransparentModal';
import { ConfirmLocationLayout } from './subcomponents';

import type { DeliveryLocationCardProps, RestaurantLocationCardProps } from './subcomponents';

export const ConfirmLocation = (props: ConfirmLocationProps) => {
  const { isFetching, restaurant, hasError, region, onClose, ...restProps } = props;
  const { isLoading, onContinue, onMoreLocations } = restProps;

  const { formatMessage } = useIntl();
  const isLiquidGlassEnabled = useIsLiquidGlassEnabled();

  if (isFetching) {
    return (
      <>
        <ConfirmLocationContainer onClose={onClose}>
          <View style={styles.notLoadedView}>
            <LoadingAnimation />
          </View>
        </ConfirmLocationContainer>

        <ConfirmLocationFloatingControls onClose={onClose} />
      </>
    );
  }

  if (hasError || !restaurant || !region) {
    return (
      <>
        <ConfirmLocationContainer onClose={onClose}>
          <View style={styles.notLoadedView}>
            <NotFoundView withoutFooter />
          </View>
        </ConfirmLocationContainer>

        <ConfirmLocationFloatingControls onClose={onClose} />
      </>
    );
  }

  return (
    <>
      <ConfirmLocationContainer onClose={onClose}>
        <ConfirmLocationLayout region={region} restaurant={restaurant} {...restProps} />
      </ConfirmLocationContainer>

      <ConfirmLocationFloatingControls onClose={onClose}>
        <View style={styles.navbarButtonsContainer}>
          {onMoreLocations ? (
            <Button
              size="large"
              palette="secondary"
              disabled={isLoading}
              style={styles.navbarSecondaryButton}
              testID="confirm-location.more-locations"
              accessibilityLabel={formatMessage(messages.locationMoreLocations)}
              onPress={onMoreLocations}
            >
              {formatMessage(messages.locationMoreLocations)}
            </Button>
          ) : null}

          <Navbar.Button
            isLiquidGlassEnabled={isLiquidGlassEnabled}
            title={formatMessage(messages.locationContinue)}
            disabled={isLoading}
            style={styles.navbarButton}
            onPress={onContinue}
          />
        </View>
      </ConfirmLocationFloatingControls>
    </>
  );
};

const ConfirmLocationContainer = (props: ViewProps & Pick<ConfirmLocationProps, 'onClose'>) => {
  const { children, onClose } = props;

  const { match, minWidth } = useResponsive();
  const { formatMessage } = useIntl();
  const isFoldableDevice = useIsFoldableDevice();

  // ─── Derived ─────────────────────────────────────────────────────────

  const shouldApplyTopSafeArea = Platform.OS === 'android' || isFoldableDevice;
  const mobileSafeAreaEdges = shouldApplyTopSafeArea
    ? MOBILE_SAFE_AREA_EDGES_WITH_TOP
    : MOBILE_SAFE_AREA_EDGES;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal
      testID="confirm-location"
      accessibilityLabel={formatMessage(messages.dialogA11yLabel)}
      size={match(['full', 'large'])}
      onClose={onClose}
      mobileEdges={mobileSafeAreaEdges}
    >
      {minWidth.isSM ? <Modal.FloatingCloseBtn onPress={onClose} style={styles.closeBtn} /> : null}

      {children}
    </TransparentModal>
  );
};

const ConfirmLocationFloatingControls = (props: FloatingControlsProps) => {
  const { children, onClose } = props;

  const { currentBreakpoint } = useResponsive();
  const isLiquidGlassEnabled = useIsLiquidGlassEnabled();

  // ─────────────────────────────────────────────────────────────────────

  if (!currentBreakpoint.isXS) {
    return null;
  }

  return (
    <FloatingBar>
      <FloatingControls isLiquidGlassEnabled={isLiquidGlassEnabled} onBackButtonPress={onClose}>
        {children}
      </FloatingControls>
    </FloatingBar>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const MOBILE_SAFE_AREA_EDGES: SafeAreaViewProps['edges'] = [];
const MOBILE_SAFE_AREA_EDGES_WITH_TOP: SafeAreaViewProps['edges'] = ['top'];

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  dialogA11yLabel: {
    defaultMessage: 'Confirm location',
    description: 'Confirm location | Modal | Dialog a11y label',
  },
  locationContinue: {
    defaultMessage: 'Continue',
    description: 'Location | Continue',
  },
  locationMoreLocations: {
    defaultMessage: 'More locations',
    description: 'Location | More Locations',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  notLoadedView: {
    flex: 1,
    paddingVertical: theme.spacing['6'],
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  closeBtn: {
    marginTop: theme.spacing['6'],
  },
  navbarButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
  },
  navbarSecondaryButton: {
    height: 56,
    borderRadius: 28,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  navbarButton: {
    width: 'auto',
    maxWidth: '100%',
    paddingHorizontal: theme.spacing['6'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type FloatingControlsProps = PropsWithChildren<Pick<ConfirmLocationProps, 'onClose'>>;

type ConfirmLocationProps = Readonly<{
  isFetching: boolean;
  hasError: boolean;
  region?: Region | null;
  restaurant?: RestaurantLocationCardProps['restaurant'] | null;
  deliveryAddress?: DeliveryLocationCardProps['deliveryAddress'] | null;
  onClose: () => void;
}> &
  Omit<ComponentProps<typeof ConfirmLocationLayout>, 'restaurant' | 'region' | 'deliveryAddress'>;
